import type { NBPRank, NextBestProductResponse } from '../../../types/next-best-product';
import type { ProductCatalogItem } from '../../../types/product-catalog';

const NBP_KEY_RX = /^NBP_\d+$/;

export const normalize = (value: unknown): string => String(value).trim().toLowerCase();

export const rankOrInfinity = (value: unknown): number =>
  Number.isFinite(value) ? (value as number) : Number.MAX_SAFE_INTEGER;

const firstNbpLabel = (rank: NBPRank): string | null => {
  for (const [key, value] of Object.entries(rank)) {
    if (NBP_KEY_RX.test(key) && typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return null;
};

export const buildProductByNbp = (products: Array<ProductCatalogItem>): Map<string, ProductCatalogItem> => {
  const map = new Map<string, ProductCatalogItem>();

  for (const product of products) {
    if (!product.nbp) continue;

    const key = normalize(product.nbp);
    if (!key || map.has(key)) continue;

    map.set(key, product);
  }

  return map;
};

export const mapApiResponseToProductIds = (
  response: NextBestProductResponse | null | undefined,
  products: Array<ProductCatalogItem>,
): Array<string> => {
  if (!response || !Array.isArray(response.NBP)) return [];

  const productByNbp = buildProductByNbp(products);

  const ranked = response.NBP.map((rank, index) => ({ rank, index }))
    .filter(({ rank }) => rank.show === true)
    .sort((a, b) => {
      const rankA = rankOrInfinity(a.rank.RANK);
      const rankB = rankOrInfinity(b.rank.RANK);
      return rankA === rankB ? a.index - b.index : rankA - rankB;
    });

  const productIds: Array<string> = [];
  const seenIds = new Set<string>();

  for (const { rank } of ranked) {
    const label = firstNbpLabel(rank);
    if (!label) continue;

    const product = productByNbp.get(normalize(label));
    if (!product || seenIds.has(product.id)) continue;

    seenIds.add(product.id);
    productIds.push(product.id);
  }

  return productIds;
};
